import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { CalendarView } from './CalendarView';
import { TimeSlotPicker } from './TimeSlotPicker';
import { useDoctorAvailabilityCalendar, useAvailableSlots } from '@/hooks';

interface RescheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newSlotId: string) => void;
  isLoading?: boolean;
  doctorId: string;
  currentDate?: string;
}

export function RescheduleDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  doctorId,
}: RescheduleDialogProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  const today = new Date().toISOString().split('T')[0] ?? '';
  const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0] ?? '';

  const { data: calendarData, isLoading: isLoadingCalendar } = useDoctorAvailabilityCalendar(
    doctorId,
    today,
    thirtyDaysFromNow
  );

  const { data: slotsData, isLoading: isLoadingSlots } = useAvailableSlots(
    doctorId,
    selectedDate ?? ''
  );

  useEffect(() => {
    if (calendarData?.dates) {
      const availableDate = calendarData.dates.find((d) => d.available);
      if (availableDate && !selectedDate) {
        setSelectedDate(availableDate.date);
      }
    }
  }, [calendarData, selectedDate]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedSlotId(null);
    }
  }, [isOpen]);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedSlotId(null);
  };

  const handleConfirm = () => {
    if (selectedSlotId) {
      onConfirm(selectedSlotId);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Reagendar consulta</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Selecione uma data</h4>
              {isLoadingCalendar ? (
                <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
              ) : (
                <CalendarView
                  availability={calendarData?.dates || []}
                  selectedDate={selectedDate}
                  onSelectDate={handleDateSelect}
                  from={today}
                  to={thirtyDaysFromNow}
                />
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Horários disponíveis
                {selectedDate && (
                  <span className="text-gray-500 font-normal ml-2">
                    ({new Date(selectedDate).toLocaleDateString('pt-BR')})
                  </span>
                )}
              </h4>
              {selectedDate ? (
                <TimeSlotPicker
                  slots={slotsData?.slots || []}
                  selectedSlotId={selectedSlotId}
                  onSelectSlot={setSelectedSlotId}
                  isLoading={isLoadingSlots}
                />
              ) : (
                <p className="text-sm text-gray-500 py-4">
                  Selecione uma data para ver os horários disponíveis.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} isLoading={isLoading} disabled={!selectedSlotId}>
            Confirmar reagendamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
